// ============================================================================
// AFB-0075 / PR 5 — the packets a person could be sent, loaded once.
//
// The right rail on an onboarding record has to answer "is there a packet for
// this person at all?" BEFORE the button is drawn, because a disabled control
// with no stated reason is indistinguishable from a broken one. That answer
// needs three tables, so it is loaded here rather than inside the modal — the
// modal opens on a decision that has already been made.
//
// useEffect + a cancelled flag, the house pattern. This app does not use
// TanStack Query. It deliberately does NOT re-run on a tick or a refetch: the
// picker holds the sender's per-send choices, and a loader that re-ran under it
// is how typed state gets destroyed.
// ============================================================================

import { useEffect, useState } from 'react';
import { errorMessage } from '../../shared/errors';
import { listTemplates } from './envelope-lib';
import { listAllPacketDocuments, listPackets } from './packet-lib';
import type { LaunchPacketOption } from './packet-launch';

export interface UsePacketLaunchOptions {
  /** Active packets only, each with its member documents in sort order. */
  options: LaunchPacketOption[];
  loading: boolean;
  error: string | null;
}

/**
 * @param enabled false for a record that could never launch a packet (a
 *        subcontractor company row), so its detail view does not pay for three
 *        queries it cannot use.
 */
export function usePacketLaunchOptions(enabled = true): UsePacketLaunchOptions {
  const [options, setOptions] = useState<LaunchPacketOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [packets, documents, templates] = await Promise.all([
          listPackets(),
          listAllPacketDocuments(),
          listTemplates(),
        ]);
        if (cancelled) return;
        // A template that has been deactivated or deleted still leaves its join
        // row behind. It is listed under its id rather than hidden: a packet
        // quietly one document short is the failure this whole feature exists
        // to avoid, and the plan will refuse it out loud at generation time.
        const names = new Map(templates.map((t) => [t.template_id, t]));
        setOptions(
          packets
            .filter((p) => p.is_active)
            .map((p) => ({
              packet_id: p.packet_id,
              name: p.name,
              is_active: p.is_active,
              documents: documents
                .filter((d) => d.packet_id === p.packet_id)
                .map((d) => ({
                  link_id: d.link_id,
                  template_id: d.template_id,
                  name: names.get(d.template_id)?.name ?? d.template_id,
                  sort_order: d.sort_order,
                  is_required: d.is_required,
                  page_count: names.get(d.template_id)?.page_count ?? null,
                  applies_to: d.applies_to,
                  applies_to_states: d.applies_to_states,
                })),
            })),
        );
      } catch (e) {
        if (!cancelled) {
          setError(errorMessage(e, 'Could not load the onboarding packets.'));
          setOptions([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { options, loading, error };
}
